import { BiDatasetCatalog } from '@euler/model/bi/catalog';
import { BiUploadedDatasetView } from '@euler/model/bi/dataset';
import { BiDatasetList, BiDatasetSection } from '@euler/model/bi/library';
import { BiDatasetRefKind } from '@euler/model/bi/query';
import { BiUploadView } from '@euler/model/bi/upload';
import { useServiceFactory } from '@euler/services/factory';
import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useEffect, useState } from 'react';

export interface BiLibrary {
  mine: BiUploadedDatasetView[];
  /** The caller's uploads still in progress, or failed. */
  uploads: BiUploadView[];
  shared: BiUploadedDatasetView[];
  /** The built-in datasets, by model key. */
  builtin: Array<{ model: string; catalog: BiDatasetCatalog }>;
}

export type BiLibraryState =
  | { kind: 'loading' }
  | { kind: 'ready'; library: BiLibrary }
  | { kind: 'failed' };

function uploadedOf(list: BiDatasetList): BiUploadedDatasetView[] {
  return list.datasets.flatMap(entry =>
    entry.kind === BiDatasetRefKind.Upload ? [entry.dataset] : [],
  );
}

/**
 * The data library's sections, read on every visit: a dataset renamed,
 * shared or deleted on its own page changes them.
 */
export function useBiLibrary() {
  const { biService } = useServiceFactory();
  const [state, setState] = useState<BiLibraryState>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const load = useCallback(() => {
    let active = true;
    Promise.all([
      biService.listDatasets({ section: BiDatasetSection.Mine }),
      // Without them the page still lists the datasets.
      biService.uploads().catch((error: unknown) => {
        console.error('BI uploads failed', error);
        return [];
      }),
      biService.listDatasets({ section: BiDatasetSection.Shared }),
      biService.listDatasets({ section: BiDatasetSection.Builtin }),
    ])
      .then(([mine, uploads, shared, builtin]) => {
        if (!active) return;
        setState({
          kind: 'ready',
          library: {
            mine: uploadedOf(mine),
            uploads,
            shared: uploadedOf(shared),
            builtin: builtin.datasets.flatMap(entry =>
              entry.kind === BiDatasetRefKind.Sql &&
              entry.catalog.dataset.kind === BiDatasetRefKind.Sql
                ? [
                    {
                      model: entry.catalog.dataset.model,
                      catalog: entry.catalog,
                    },
                  ]
                : [],
            ),
          },
        });
      })
      .catch(error => {
        console.error('BI library failed', error);
        if (active)
          setState(current =>
            current.kind === 'ready' ? current : { kind: 'failed' },
          );
      });
    return () => {
      active = false;
    };
  }, [biService]);
  useFocusEffect(load);
  useEffect(() => {
    if (reload) return load();
  }, [load, reload]);
  const refresh = useCallback(() => {
    setState(current =>
      current.kind === 'failed' ? { kind: 'loading' } : current,
    );
    setReload(value => value + 1);
  }, []);
  return { state, refresh };
}
